import React, { useCallback, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { Autocomplete } from "components/Autocomplete";
import { Machine } from "interfaces/machine";
import { Site } from "interfaces/site";
import { CaseFilter } from "interfaces/case-filter";
import { useMachinesQuery } from "hooks/queries/useMachinesQuery";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface OptionType extends CaseFilter {
  siteIds: number[];
}

interface MachineFilterProps {
  selectedMachines: Machine[];
  selectedSites: Site[];
  onChangeSelectedMachines: (values: Machine[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  name: "Select All",
  siteIds: [0],
  ids: [0]
};

export const MachineFilter = ({
  selectedMachines,
  selectedSites,
  onChangeSelectedMachines,
  showChips = true
}: MachineFilterProps): JSX.Element => {
  const {
    isLoading: machinesLoading,
    error: machinesError,
    data: _machines
  } = useMachinesQuery();
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  // ensure variable is defined for typescript
  const machines = useMemo(() => _machines?.results ?? [], [_machines]);

  const onFocus = useCallback(() => {
    if (!selectedMachines.length) {
      setSelectedState([]);
      return;
    }
    setSelectedState(selectedMachines as any);
  }, [selectedMachines]);

  const options = useMemo(() => {
    /* If there are machines with the same name then only the first one found will remain in the machines list,
        the id of each subsequent duplicate machine is added to the 'ids' field of the first one found.
        the site id of each subsequent duplicate machine is added to the 'site_ids' field of the first one found. */
    let filteredMachines = machines.reduce(
      (results: OptionType[], machine): OptionType[] => {
        const duplicatedTargetIndex = results.findIndex(
          el => el.name === machine.name
        );
        if (duplicatedTargetIndex !== -1) {
          results[duplicatedTargetIndex].ids = [
            ...(results[duplicatedTargetIndex].ids || []),
            machine.id
          ];
          results[duplicatedTargetIndex].siteIds = [
            ...(results[duplicatedTargetIndex].siteIds || []),
            machine.site
          ];
        } else {
          results.push({
            name: machine.name,
            siteIds: [machine.site],
            ids: [machine.id]
          });
        }
        return results;
      },
      []
    );

    if (machines.length > 0 && selectedSites && selectedSites.length > 0) {
      const siteIds = selectedSites.map(({ id }) => id) ?? [];
      filteredMachines = filteredMachines.filter(
        machine =>
          machine.siteIds &&
          siteIds.some(siteId => machine.siteIds.includes(siteId))
      );
    }

    return [selectAllOption, ...filteredMachines];
  }, [selectedSites, machines]);

  return (
    <StyledAutocomplete
      label="Machine"
      placeholder={showChips ? "Machine" : ""}
      loading={machinesLoading}
      error={machinesError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options}
      noOptionsText="No results found"
      getOptionLabel={(option: Machine) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedMachines}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: Machine,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option =>
            selectedState.some(
              selected => selected.ids?.join("_") === option.ids?.join("_")
            )
        );

        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: Machine[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option =>
            selectedState.some(
              selected => selected.ids?.join("_") === option.ids?.join("_")
            )
        );

        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              // option => !filteredSelectedState.includes(option)
              option =>
                !filteredSelectedState.some(
                  selected => selected.ids?.join("_") === option.ids?.join("_")
                )
            );
            onChangeSelectedMachines(selected as any);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedMachines, ...filteredNonSelectAllOptions],
              // @ts-ignore
              item => item.ids.join("")
            );

            // If not everything was selected, select all
            onChangeSelectedMachines(selected as any);
            setSelectedState(selected as any);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedMachines(values);
          setSelectedState(values as any);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
